import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MammothMascot } from "../components/MammothMascot";
import { BrandLogo } from "../components/BrandLogo";
import { StageDetailModal } from "../components/StageDetailModal";
import { WhyMammothModal } from "../components/WhyMammothModal";
import { RepDetailModal } from "../components/RepDetailModal";
import { LEVELS } from "../lib/levels";
import { ArrowRight, Wind, RefreshCcw, BookOpen, Anchor, Heart, Eye } from "lucide-react";

export default function Landing() {
  const [activeStage, setActiveStage] = useState(null);
  const [showWhy, setShowWhy] = useState(false);
  const [activeRep, setActiveRep] = useState(null);
  // transition: "image" → splash mammoth-coach for 2s
  //             "video-in" → video fades in
  //             "video-out" → video fades out before navigation
  const [transitionPhase, setTransitionPhase] = useState(null);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const transitionVideoRef = useRef(null);
  const navigate = useNavigate();

  const startTransition = () => {
    setTransitionProgress(0);
    setTransitionPhase("image");
  };
  const goToAuth = () => navigate("/auth?mode=signup");

  // Splash → video-in sequencing (2s mammoth-coach hold).
  useEffect(() => {
    if (transitionPhase !== "image") return;
    const t = setTimeout(() => setTransitionPhase("video-in"), 2000);
    return () => clearTimeout(t);
  }, [transitionPhase]);

  // Splash progress: 0% → 15% over 2 seconds.
  useEffect(() => {
    if (transitionPhase !== "image") return;
    const start = Date.now();
    let raf;
    const tick = () => {
      const t = Math.min((Date.now() - start) / 2000, 1);
      setTransitionProgress(t * 15);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [transitionPhase]);

  // Video-out progress: 95% → 100% over the 1.5s soft fade-out.
  useEffect(() => {
    if (transitionPhase !== "video-out") return;
    const start = Date.now();
    let raf;
    const tick = () => {
      const t = Math.min((Date.now() - start) / 1500, 1);
      setTransitionProgress(95 + t * 5);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [transitionPhase]);

  // Fade-out trigger 1.6s before the video naturally ends.
  const handleVideoTimeUpdate = (e) => {
    const v = e.currentTarget;
    if (!v.duration) return;
    const videoT = Math.min(v.currentTime / v.duration, 1);
    // Video phase drives the progress from 15% → 95%.
    setTransitionProgress(15 + videoT * 80);
    if (transitionPhase === "video-out") return;
    if (v.currentTime >= v.duration - 1.6) setTransitionPhase("video-out");
  };
  return (
    <div className="min-h-screen relative grain" style={{ backgroundColor: "#F5F5F0", color: "#0e203b" }} data-testid="landing-page">
      <style>{`
        /* Login circle — slow, peaceful breathing pulse */
        @keyframes mlSigninPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(14,32,59,0.45); }
          50%      { box-shadow: 0 0 0 14px rgba(14,32,59,0);   }
        }
        .ml-signin-pulse { animation: mlSigninPulse 5.2s ease-in-out infinite; }
        @keyframes mlSplashIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        /* Transition video: cover on phones (no bottom gap), contain on
           tablet+desktop (faces don't get cropped). */
        .ml-transition-video { object-fit: contain; }
        @media (max-width: 640px) {
          .ml-transition-video { object-fit: cover; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ml-signin-pulse { animation: none !important; }
        }
        /* Phone-only: logo sized for mobile balance */
        @media (max-width: 640px) {
          [data-testid="landing-brand-logo"] img { width: 260px !important; }
          [data-testid="top-bar-eg"] { transform: scale(0.88) !important; }
        }
      `}</style>
      {/* top utility bar */}
      <div
        data-testid="landing-top-bar"
        className="w-full"
        style={{ backgroundColor: "#d6e0d8", height: "clamp(37px, 4.6vh, 55px)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          <div
            className="flex items-center justify-center"
            style={{ width: "clamp(260px, 32vw, 454px)" }}
          >
            <img
              src="/eg.png"
              alt="Emotional Gym"
              data-testid="top-bar-eg"
              style={{ display: "block", transform: "scale(0.8)", transformOrigin: "center" }}
            />
          </div>
          <Link
            to="/auth"
            data-testid="header-signin"
            aria-label="Sign in"
            className="ml-signin-pulse btn-pill bg-[#0e203b] text-white rounded-full w-9 h-9 flex items-center justify-center"
          >
            <svg viewBox="0 0 1024 1024" width="16" height="16" aria-hidden="true">
              <path
                fill="#f2f3ee"
                d="M624.112 547.68c9.76-8.768 15.888-21.52 15.888-35.68s-6.144-26.896-15.888-35.68l-160-144a48 48 0 0 0-64.224 71.36L466.912 464H176a48 48 0 1 0 0 96h290.912l-67.024 60.32a48 48 0 1 0 64.224 71.36l160-144zM896 510.912V142.96a48 48 0 0 0-48-48H608a48 48 0 1 0 0 96h192v640H608a48 48 0 1 0 0 96h240a48 48 0 0 0 48-48V510.992v-0.048-0.032z"
              />
            </svg>
          </Link>
        </div>
      </div>
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 flex items-start justify-between">
        <Link to="/" aria-label="mammoth-love home" data-testid="landing-brand-logo">
          <BrandLogo width="clamp(260px, 32vw, 454px)" />
        </Link>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[28px] sm:pt-4 pb-[34px] grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7 animate-fadeUp">
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl mt-2 leading-[0.95] tracking-tighter">
            Train your feelings.<br />
            <span className="italic font-medium" style={{ fontSize: "0.49em", color: "#959995" }}>Mind Flexes, for Social Challenges !</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl max-w-xl leading-relaxed" style={{ color: "#0e203b" }}>
            <strong className="font-bold" style={{ fontSize: "1.1em" }}>Daily reps for the stuff school never taught you.</strong>
            <span className="block" style={{ color: "#959995", marginTop: "10px" }}>Mood check-ins, breathwork, grounding, reframes — paced for where you actually are.</span>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={startTransition}
              data-testid="hero-cta-start"
              className="btn-pill bg-[#0e203b] text-white rounded-full px-7 h-14 font-bold flex items-center gap-2"
            >
              Start training <ArrowRight size={18} />
            </button>
            <Link to="/auth" data-testid="hero-cta-login" className="btn-pill border-2 border-[#0e203b]/10 rounded-full px-7 h-14 font-bold flex items-center" style={{ backgroundColor: "#d6e0d8" }}>
              I already train here
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 text-xs font-semibold opacity-70">
            <span>Mood check-ins</span><span>·</span>
            <span>Box breathing</span><span>·</span>
            <span>5-4-3-2-1 grounding</span><span>·</span>
            <span>CBT reframes</span><span>·</span>
            <span>Neuroscience-powered coach</span>
          </div>
        </div>
        <div className="md:col-span-5 flex flex-col items-center gap-5">
          <div className="relative w-full flex justify-center">
            <div className="absolute -inset-10 rounded-[3rem] bg-white/60 blur-3xl"></div>
            <div
              className="relative overflow-hidden bg-white"
              style={{
                width: "100%",
                maxWidth: 560,
                aspectRatio: "1824 / 1136",
                borderRadius: "clamp(28px, 4vw, 48px)",
                boxShadow: "0 30px 60px rgba(14,32,59,0.18), 0 8px 16px rgba(14,32,59,0.10)",
                border: "1px solid rgba(14,32,59,0.06)",
              }}
            >
              <video
                src="/mammoth-green.mp4"
                autoPlay
                loop
                muted
                playsInline
                data-testid="landing-hero-video"
                aria-label="Mammoth motion loop"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center center",
                  display: "block",
                }}
              />
              {/* Inner shadow overlay — depth + occlusion on top of video */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  pointerEvents: "none",
                  boxShadow:
                    "inset 0 4px 18px rgba(14,32,59,0.30), inset 0 -6px 22px rgba(14,32,59,0.20), inset 0 0 0 1px rgba(14,32,59,0.06)",
                }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowWhy(true)}
            data-testid="why-mammoth-trigger"
            className="btn-pill rounded-full font-bold uppercase border-2 border-[#0e203b]/10 flex items-center justify-center transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              backgroundColor: "#d6e0d8",
              color: "#0e203b",
              paddingInline: "clamp(18px, 2vw, 28px)",
              height: "clamp(26px, 2.8vw, 32px)",
              fontSize: "clamp(11px, 1.05vw, 13px)",
              fontWeight: 800,
              letterSpacing: "0.18em",
              boxShadow: "0 8px 20px rgba(214,224,216,0.45)",
              marginTop: "10px",
            }}
          >
            Why a mammoth ?
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">Five stages. Five vibes.</h2>
        <p className="max-w-2xl mb-8 text-neutral-700">Your background shifts with you. As you grow, the space changes — matched to the tone you actually resonate with.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {LEVELS.map(l => (
            <div
              key={l.level}
              data-testid={`stage-card-${l.level}`}
              className="relative rounded-3xl p-5 shadow-sm border border-[#0e203b]/5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg group"
              style={{ backgroundColor: l.background, color: l.text }}
            >
              <div className="text-xs font-bold uppercase tracking-widest opacity-60">Stage {l.level}</div>
              <div className="font-display font-bold text-xl mt-1">{l.name}</div>
              <div className="text-xs mt-2 opacity-70">{l.aesthetic}</div>
              <button
                type="button"
                onClick={() => setActiveStage(l)}
                data-testid={`stage-eye-${l.level}`}
                aria-label={`Explore ${l.name}`}
                className="mt-4 h-8 w-full rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-x-105 origin-left hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer"
                style={{ backgroundColor: l.accent, color: l.level === 1 ? "#0e203b" : l.background }}
              >
                <Eye size={18} strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-2">Your daily reps</h2>
        <p className="max-w-2xl mb-8 text-neutral-700">Training Sessions & Chart Reports</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { icon: Heart, label: "Mood check-in", t: "mood" },
            { icon: Wind, label: "Box breathing", t: "breathing" },
            { icon: Anchor, label: "5-4-3-2-1", t: "grounding" },
            { icon: RefreshCcw, label: "Reframes", t: "reframes" },
            { icon: BookOpen, label: "Journal", t: "journal" },
          ].map(({ icon: I, label, t }) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveRep(t)}
              data-testid={`rep-${t}`}
              aria-label={`Preview ${label} daily rep`}
              className="group text-left bg-white rounded-3xl p-5 border border-[#0e203b]/5 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:bg-[#9bd5d7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0e203b]/40 cursor-pointer"
            >
              <I size={24} strokeWidth={2.5} />
              <div className="font-bold mt-3">{label}</div>
            </button>
          ))}
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 text-sm opacity-60">
        Not a replacement for therapy. A daily gym for your inner weather.
      </footer>

      {activeStage && <StageDetailModal stage={activeStage} onClose={() => setActiveStage(null)} />}
      {showWhy && <WhyMammothModal onClose={() => setShowWhy(false)} />}
      {activeRep && <RepDetailModal repKey={activeRep} onClose={() => setActiveRep(null)} />}
      {transitionPhase && (
        <div
          data-testid="signup-transition-overlay"
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{
            width: "100vw",
            height: "100dvh",
            minHeight: "100vh",
            margin: 0,
            padding: 0,
            backgroundColor: "#f2f3ee",
          }}
        >
          {/* Branded splash band — full-width #d6e0d8 bar @ 50% opacity
              behind the mammoth-coach silhouette. Sits 30px below the
              vertical center, with 10px breathing room above + below the
              image. Stays in place for the whole transition. */}
          <div
            data-testid="signup-transition-splash-wrap"
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "calc(50% + 30px)",
              left: 0,
              right: 0,
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 10,
              paddingBottom: 10,
              zIndex: 2,
              pointerEvents: "none",
              animation: transitionPhase === "image" ? "mlSplashIn 700ms ease-out both" : "none",
            }}
          >
            {/* Sage bar behind the splash, between video and image */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#d6e0d8",
                opacity: 0.5,
                transform: "scaleY(0.64)",
                transformOrigin: "center",
              }}
            />
            {/* Progress bar — sits at the bottom edge of the scaled sage
                bar (which ends at 18% from the wrapper bottom). Loads
                left → right across the splash + video duration so users
                don't get anxious or try to click. */}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "18%",
                height: 4,
                overflow: "hidden",
                zIndex: 1,
              }}
              aria-hidden="true"
            >
              <div
                data-testid="signup-transition-progress"
                style={{
                  height: "100%",
                  width: `${transitionProgress}%`,
                  backgroundColor: "#9bd5d7",
                  transition: "width 220ms linear",
                  borderRadius: "0 9999px 9999px 0",
                }}
              />
            </div>
            {/* The mammoth-coach silhouette at full opacity */}
            <img
              src="/mammoth-coach.png"
              alt=""
              data-testid="signup-transition-splash"
              style={{
                position: "relative",
                maxWidth: "42vw",
                maxHeight: "42vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                filter: "drop-shadow(0 24px 48px rgba(14,32,59,0.18)) drop-shadow(0 8px 16px rgba(14,32,59,0.12))",
              }}
            />
          </div>
          {/* Phase 2 — video appears at full target opacity behind the
              splash. Only the splash fades out, dissolving over the already-
              playing video for a clean cinematic hand-off. */}
          {transitionPhase !== "image" && (
            <video
              ref={transitionVideoRef}
              src="/transition.mp4"
              autoPlay
              muted
              playsInline
              data-testid="signup-transition-video"
              className="ml-transition-video"
              onTimeUpdate={handleVideoTimeUpdate}
              onEnded={goToAuth}
              onError={goToAuth}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
                opacity: transitionPhase === "video-out" ? 0 : 0.5,
                transition: transitionPhase === "video-out" ? "opacity 1500ms ease-in-out" : "none",
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
